import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import axios from "../../utils/axiosInstance";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { sortAlphabetically } from "../../utils/sortAlphabetically";
import { getUIDescription } from "../SiteAdmin/customerGroupDetails/AssigningCustomerGroupDevice";

export default function EditUnitForm({
  open,
  onClose,
  onSubmit,
  unitData,
  onUpdateSuccess,
}) {
  const [unitName, setUnitName] = useState("");
  const [unitId, setUnitId] = useState("");

  //the first charged
  const [parentGroupId, setParentGroupId] = useState("");
  const [regionDistrictId, setRegionDistrictId] = useState("");
  const [routeId, setRouteId] = useState("");
   //the first charged
  const [parentGroupName, setParentGroupName] = useState("");
  const [regionDistrictName, setRegionDistrictName] = useState("");
  const [routeName, setRouteName] = useState("");
  //the new selected for change
  const [newParentGroupId, setNewParentGroupId] = useState("");
  const [newRegionDistrictId, setNewRegionDistrictId] = useState("");
  const [newRouteId, setNewRouteId] = useState("");
   //the new selected for change
  const [newParentGroupName, setNewParentGroupName] = useState("");
  const [newRegionDistrictName, setNewRegionDistrictName] = useState("");
  const [newRouteName, setNewRouteName] = useState("");

  // State for dropdown data
  const [groups, setGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [allRegions, setAllRegions] = useState([]);
  const [loadingRegions, setLoadingRegions] = useState(false);
  const [allRoutes, setAllRoutes] = useState([]);
  const [loadingRoute, setLoadingRoutes] = useState(false);

  // Loading state for update operation
  const [isUpdating, setIsUpdating] = useState(false);

  // Pre-populate form fields when unitData changes
  useEffect(() => {
    const initializeForm = async () => {
      if (unitData) {
        setUnitName(unitData.name || "");
        setUnitId(unitData.id || "");
        // Fallback: fetch from assets if groupId not in unitData
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_API_GATEAWAY}/devices/details/${unitData.id}`
          );
          if (response.data.assets.length > 0) {
            setParentGroupId(response.data.assets[0].group.id);
            setNewParentGroupId(response.data.assets[0].group.id);
            setRegionDistrictId(response.data.assets[0].region.id);
            setNewRegionDistrictId(response.data.assets[0].region.id);
            setRouteId(response.data.assets[0].route.id);
            setNewRouteId(response.data.assets[0].route.id);
            setParentGroupName(response.data.assets[0].group.name);
            setNewParentGroupName(response.data.assets[0].group.name);
            setRegionDistrictName(response.data.assets[0].region.name);
            setNewRegionDistrictName(response.data.assets[0].region.name);
            setRouteName(response.data.assets[0].route.name);
            setNewRouteName(response.data.assets[0].route.name);
          }
        } catch (error) {
          console.error("Error fetching assets:", error);
        }
      }
    };

    initializeForm();
  }, [unitData]);

  // Fetch groups on mount
  useEffect(() => {
    const fetchGroups = async () => {
      try {
        setLoadingGroups(true);
        const response = await axios.get(
          `${process.env.REACT_APP_API_GATEAWAY}/groups`,
          {
            params: { companyId: process.env.REACT_APP_COMPANY_ID },
          }
        );
        const sortedData = sortAlphabetically(response.data, "name");
        setGroups(sortedData);
      } catch (err) {
        console.error("❌ Error fetching groups:", err);
      } finally {
        setLoadingGroups(false);
      }
    };

    if (open) {
      fetchGroups();
    }
  }, [open]);

  // Fetch regions when parent group changes
  useEffect(() => {
    if (!newParentGroupId) {
      setAllRegions([]);
      setNewRegionDistrictId("");
      setAllRoutes([]);
      setNewRouteId("");
      return;
    }

    // Clear region and routes when group changes (except on initial load)
    if (newParentGroupId !== parentGroupId) {
      setNewRegionDistrictId("");
      setAllRoutes([]);
      setNewRouteId("");
    }

    const fetchRegions = async () => {
      try {
        setLoadingRegions(true);
        const response = await axios.get(
          `${process.env.REACT_APP_API_GATEAWAY}/regions?groupId=${newParentGroupId}`,
          {
            params: { companyId: process.env.REACT_APP_COMPANY_ID },
          }
        );
        setAllRegions(response.data);
        console.log(
          "✅ Regions loaded for group:",
          newParentGroupId,
          response.data
        );
      } catch (err) {
        console.error("❌ Error fetching regions:", err);
        setAllRegions([]);
      } finally {
        setLoadingRegions(false);
      }
    };

    fetchRegions();
  }, [newParentGroupId, parentGroupId]);

  // Fetch technicians when region changes
  useEffect(() => {
    if (!newRegionDistrictId) {
      setAllRoutes([]);
      setNewRouteId("");
      return;
    }

    // Clear routes when region changes (except on initial load)
    if (newRegionDistrictId !== regionDistrictId) {
      setNewRouteId("");
    }

    const fetchRoutes = async () => {
      try {
        setLoadingRoutes(true);
        const response = await axios.get(
          `${process.env.REACT_APP_API_GATEAWAY}/routes?regionId=${newRegionDistrictId}`
        );
        setAllRoutes(response.data);
        console.log(
          "✅ Technicians loaded for region:",
          newRegionDistrictId,
          response.data
        );
      } catch (err) {
        console.error("❌ Error fetching technicians:", err);
        setAllRoutes([]);
      } finally {
        setLoadingRoutes(false);
      }
    };

    fetchRoutes();
  }, [newRegionDistrictId, regionDistrictId]);

  const handleUpdateDevice = async () => {
    try {
      setIsUpdating(true);

      const payload = {
        newAssets: [
          {
            groupId: newParentGroupId,
            regionId: newRegionDistrictId,
            routeId: newRouteId,
            groupName: newParentGroupName,
            regionName: newRegionDistrictName,
            routeName: newRouteName,
          },
        ],
        oldAssets: parentGroupId
          ? [
              {
                groupId: parentGroupId,
                regionId: regionDistrictId,
                routeId: routeId,
                groupName: parentGroupName,
                regionName: regionDistrictName,
                routeName: routeName,
              },
            ]
          : [],
        name: unitName || "",
        UI_Description: `${getUIDescription()}\n "EDIT Unit Button"`, 
      };

      const response = await axios.put(
        `${process.env.REACT_APP_API_GATEAWAY}/devices?id=${unitData.id}`,
        payload
      );

      console.log("✅ Device updated:", response.data);

      if (onSubmit) {
        onSubmit(response.data);
      }
      onUpdateSuccess(); //this to update the mother component to bring the lat & lon of the updated device
      toast.success("✅ Unit updated successfully!");
    } catch (error) {
      console.error(
        "❌ Error updating device:",
        error.response?.data || error.message
      );
      toast.error(error.response?.data?.message || "❌ Failed to update unit");
    } finally {
      setIsUpdating(false);
      onClose();
    }
  };

  const handleClose = () => {
    if (!isUpdating) {
      onClose();
    }
    // Reset form when closing
    setNewParentGroupId(parentGroupId);
    setNewRegionDistrictId(regionDistrictId);
    setNewRouteId(routeId);
    setUnitName(unitData.name);
    // setUnitId("");
    // setRegionDistrictId("");
    // setRouteId("");
    // setAllRegions([]);
    // setAllRoutes([]);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={isUpdating}
    >
      <DialogTitle sx={{ mt: 1, fontSize: "0.93rem" }}>
        Edit Unit: Update unit settings and assignments
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>

          <TextField
            label="Unit Name"
            value={unitName}
            onChange={(e) => setUnitName(e.target.value)}
            fullWidth
            required
            disabled={isUpdating}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 0, // ✅ remove rounding
              },
            }}
          />
          
          {/* Modem Serial Number is same as Modem Serial Number */}
          <TextField
            label="Modem Serial Number"
            value={unitId}
            slotProps={{
              input: {
                readOnly: true,
              },
            }}
            fullWidth
            disabled={isUpdating}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 0, // ✅ remove rounding
              },
            }}
          />

          

          <TextField
            select
            label="Parent Group"
            value={newParentGroupId}
            onChange={(e) => setNewParentGroupId(e.target.value)}
            fullWidth
            disabled={loadingGroups || isUpdating}
            required
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 0, // ✅ remove rounding
              },
            }}
          >
            {groups.length > 0 ? (
              groups.map((group) => (
                <MenuItem key={group.id} value={group.id}>
                  {group.name}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>
                {loadingGroups ? "Loading groups..." : "No groups available"}
              </MenuItem>
            )}
          </TextField>

          <TextField
            select
            label="Region/District"
            value={newRegionDistrictId}
            onChange={(e) => setNewRegionDistrictId(e.target.value)}
            fullWidth
            disabled={loadingRegions || !newParentGroupId || isUpdating}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 0, // ✅ remove rounding
              },
            }}
          >
            <MenuItem value="">
              <em>No Region Selected</em>
            </MenuItem>
            {allRegions.length > 0 ? (
              allRegions.map((regionItem) => (
                <MenuItem key={regionItem.id} value={regionItem.id}>
                  {regionItem.name}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>
                {loadingRegions
                  ? "Loading Regions..."
                  : newParentGroupId
                  ? "No Regions available"
                  : "Select Parent Group first"}
              </MenuItem>
            )}
          </TextField>

          <TextField
            select
            label="Technician"
            value={newRouteId}
            onChange={(e) => setNewRouteId(e.target.value)}
            fullWidth
            disabled={loadingRoute || !newRegionDistrictId || isUpdating}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 0, // ✅ remove rounding
              },
            }}
          >
            <MenuItem value="">
              <em>No Technician Assigned</em>
            </MenuItem>
            {allRoutes.length > 0 ? (
              allRoutes.map((tech) => (
                <MenuItem key={tech.id} value={tech.id}>
                  {tech.name ||
                    `${tech.firstName} ${tech.lastName}` ||
                    tech.email}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>
                {loadingRoute
                  ? "Loading technicians..."
                  : !newRegionDistrictId
                  ? "No technicians available"
                  : "Select Region first"}
              </MenuItem>
            )}
          </TextField>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          sx={{
            borderRadius: 0, // ✅ remove rounding
          }}
          onClick={handleClose}
          color="error"
          disabled={isUpdating}
        >
          Cancel
        </Button>
        <Button
          onClick={handleUpdateDevice}
          variant="contained"
          color="primary"
          disabled={!unitName.trim() || !newParentGroupId || isUpdating}
          startIcon={
            isUpdating ? <CircularProgress size={20} color="#60a5fa" /> : null
          }
          sx={{
            borderRadius: 0, // ✅ remove rounding
          }}
        >
          {isUpdating ? "Updating..." : "Update Unit"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
